/**
 * Impact — personal + global CO₂ / e-waste ESG dashboard.
 * Backend:
 *   GET /api/impact/mine       -> user's cumulative impact
 *   GET /api/impact/global     -> public counters
 *   GET /api/impact/certificate -> downloadable PDF
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Leaf, TreePine, Car, Plane, Recycle, Download, Loader2, Globe } from "lucide-react";
import { toast } from "sonner";

function Stat({ icon: Icon, big, sub, tone = "primary" }) {
  return (
    <div className={`rounded-2xl p-5 border ${tone === "primary" ? "bg-primary/5 border-primary/20" : "bg-muted/40 border-border"}`}>
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${tone === "primary" ? "bg-primary/10 text-primary" : "bg-secondary/15 text-secondary"}`}>
        <Icon className="w-5 h-5" />
      </div>
      <div className="mt-4 font-display text-3xl font-medium">{big}</div>
      <div className="text-xs text-muted-foreground mt-1">{sub}</div>
    </div>
  );
}

export default function Impact() {
  const [mine, setMine] = useState(null);
  const [globalImpact, setGlobalImpact] = useState(null);
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api.get("/impact/mine").then(r => setMine(r.data)).catch(() => {});
    api.get("/impact/global").then(r => setGlobalImpact(r.data)).catch(() => {});
  }, []);

  const download = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/impact/certificate", { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "treasurescare-impact-certificate.pdf";
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Certificate downloaded — LinkedIn-ready");
    } catch (e) {
      toast.error("Certificate download failed");
    }
    setDownloading(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="impact-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline text-secondary flex items-center gap-2"><Leaf className="w-3 h-3" /> ESG · CO₂ impact</div>
          <h1 className="text-3xl font-display font-light mt-1">Your repair, planet's win</h1>
          <p className="mt-2 text-muted-foreground max-w-xl">
            Every repair keeps materials out of landfill and stops a new device from being manufactured.
            Here's the impact your Treasurescare membership has made.
          </p>
        </div>
        <Button onClick={download} disabled={downloading} data-testid="impact-download-btn">
          {downloading
            ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Building…</>
            : <><Download className="w-4 h-4 mr-2" /> Download certificate</>}
        </Button>
      </div>

      {mine ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Stat icon={Leaf} big={`${mine.kg_co2_saved.toFixed(0)} kg`} sub="CO₂ saved vs. buying new" />
            <Stat icon={Recycle} big={`${mine.kg_e_waste_diverted.toFixed(1)} kg`} sub="e-waste diverted from landfill" />
            <Stat icon={TreePine} big={mine.equivalents.trees_planted} sub="trees' worth of annual absorption" tone="muted" />
            <Stat icon={Car} big={`${Math.round(mine.equivalents.km_car_driving_avoided).toLocaleString()} km`} sub="of car driving avoided" tone="muted" />
          </div>

          <Card>
            <CardHeader><CardTitle className="text-base">In everyday terms</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-3">
                <TreePine className="w-4 h-4 text-primary" />
                <div className="text-sm">
                  You've matched about <span className="font-semibold">{mine.equivalents.trees_planted} trees</span> worth of annual CO₂ absorption.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Plane className="w-4 h-4 text-secondary" />
                <div className="text-sm">
                  Equivalent to skipping <span className="font-semibold">{mine.equivalents.short_flights_avoided.toFixed(1)}</span> short-haul flights.
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Recycle className="w-4 h-4 text-primary" />
                <div className="text-sm">
                  Across <span className="font-semibold">{mine.repairs_completed}</span> completed repairs.
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      ) : (
        <Card><CardContent className="py-12 text-center text-muted-foreground text-sm">Calculating your impact…</CardContent></Card>
      )}

      {globalImpact && (
        <Card className="bg-primary/5 border-primary/30">
          <CardHeader><CardTitle className="text-base flex items-center gap-2"><Globe className="w-4 h-4" /> Community impact</CardTitle></CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <div className="font-display text-3xl">{globalImpact.total_repairs.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">repairs completed</div>
              </div>
              <div>
                <div className="font-display text-3xl">{Math.round(globalImpact.total_kg_co2_saved).toLocaleString()} kg</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">total CO₂ saved</div>
              </div>
              <div>
                <div className="font-display text-3xl">{globalImpact.total_kg_e_waste_diverted.toFixed(0)} kg</div>
                <div className="text-xs text-muted-foreground uppercase tracking-widest mt-1">e-waste diverted</div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
